import {UserModel} from 'app/models/UserModel'
import {NotFound} from 'app/modules/errors/NotFound'
import SchoolToInstitute from 'app/pages/school/school/SchoolToInstitute'
import {Settings} from 'app/pages/settings/Settings'
import {hasPermission, isLyceum, isM_Layout} from 'common/HelperFunctions'
import {isDevEnv} from 'common/HelperFunctions'
import SuspensedView from 'common/components/SuspensedView'
import {isNewFeePageEnabled} from 'common/helpers/fee'
import {useIsNewExamManagerEnabled} from 'common/hooks/useIsNewExamManagerEnabled'
import IframeLayout from 'common/layout/IframeLayout'
import {usePostHog} from 'posthog-js/react'
import {FC, lazy, useEffect} from 'react'
import {useSelector} from 'react-redux'
import {Navigate, Route, Routes} from 'react-router'
import {RootState} from 'setup'

import {MasterLayout} from '_metronic/layout/MasterLayout'

/*
 * CCCCC
 * C
 * C
 * C
 * CCCCC
 */
const ChatPage = lazy(() => import('common/components/chat/ChatPage'))

/*
 * DDDDD
 * D    D
 * D     D
 * D    D
 * DDDDD
 */
const Dashboard = lazy(() => import('app/pages/student/dashboard/Dashboard'))
// Same module the school side uses; the API scopes the list to the student /
// selected child and the update/delete actions are permission-gated.
const DigitalRequestPage = lazy(() => import('app/pages/school/digital-request/DigitalRequestPage'))

/*
 * EEEEEE
 * E
 * EEEE
 * E
 * EEEEEE
 */

const EventPageHighlights = lazy(() => import('app/pages/school/Event/components/Highlights'))
const EventPageView = lazy(() => import('app/pages/school/Event/components/View'))
const EventPageGallery = lazy(() => import('app/pages/school/Event/components/Gallery'))

const ExamSchedulePage = lazy(() => import('app/pages/school/ExamSchedule/ExamSchedulePage'))
/*
 * FFFFFF
 * F
 * FFFF
 * F
 * F
 */

/*
 * L
 * L
 * L
 * L
 * LLLLL
 */
const LecturePage = lazy(() => import('app/pages/school/Lecture/components/LectureList'))
const LectureViewPage = lazy(() => import('app/pages/school/Lecture/components/ViewLecture'))
const LGSFeeIframe = lazy(() => import('app/pages/student/LGSFeeIframe'))

/*
 * FFFFFF
 * F
 * FFFF
 * F
 * F
 */
const StudentFeePage = lazy(() => import('app/pages/student/Fee/FeePage'))

/*
 * N    N
 * NN   N
 * N N  N
 * N  N N
 * N   NN
 */
const StudentNotificationsPage = lazy(
  () => import('app/pages/student/Notifications/NotificationsPage')
)

// Native replacements for the remaining simple student/parent iframe pages
const StudentActivitiesPage = lazy(() => import('app/pages/student/Activities/ActivitiesPage'))
const StudentClassHistoryPage = lazy(
  () => import('app/pages/student/ClassHistory/ClassHistoryPage')
)
const StudentTutorialsPage = lazy(() => import('app/pages/student/Tutorials/TutorialsPage'))
const StudentExamsListPage = lazy(() => import('app/pages/student/Exam/StudentExamsPage'))
const StudentSubjectsPage = lazy(() => import('app/pages/student/Subject/StudentSubjectsPage'))

/*
 * N    N
 * NN   N
 * N N  N
 * N  N N
 * N   NN
 * N    N
 */

const NewsPageFullmode = lazy(() => import('app/pages/school/News/components/FullMode'))
const NewsPageView = lazy(() => import('app/pages/school/News/components/View'))

/*
 * PPPPPPP
 * P    P
 * PPPPPP
 * P
 * P
 */
const ProfilePage = lazy(() => import('app/pages/common/profile/ProfilePage'))
const PerformanceReportsPage = lazy(
  () => import('app/pages/school/PerformanceReports/PerformanceReportsPage')
)

/*
 *  SSSSS
 * S
 *  SSSSS
 *       S
 * SSSSS
 */
const SchoolPage = lazy(() => import('app/pages/school/school/SchoolPage'))
const CollegeCounsellingPage = lazy(
  () => import('app/pages/school/CollegeCounselling/CollegeCounsellingPage')
)
const StudentExamsPage = lazy(() => import('app/pages/student/StudentExams/StudentExamsPage'))
const StudentProfilePage = lazy(() => import('app/pages/student/StudentProfile/StudentProfilePage'))
const UserManualPage = lazy(() => import('app/pages/userManual/UserManualPage'))
const SchedulePageYearly = lazy(
  () => import('app/pages/school/Schedule/components/yearly/YearlyCalendar')
)
const SchedulePageDaily = lazy(
  () => import('app/pages/school/Schedule/components/daily/DailySchedule')
)
const SchedulePageHighlights = lazy(
  () => import('app/pages/school/Schedule/components/yearly/Highlights')
)
const SchedulePageYearlyView = lazy(
  () => import('app/pages/school/Schedule/components/yearly/ViewYearly')
)
const SchedulePageDailyView = lazy(
  () => import('app/pages/school/Schedule/components/daily/ViewDaily')
)
const StudentSubjectCombinedAttendanceReport = lazy(
  () =>
    import('app/pages/school/Attendance/components/utils/StudentSubjectCombinedAttendanceReport')
)

const StudentRoutes: FC = () => {
  const posthog = usePostHog()
  const user = useSelector<RootState>((state) => state.userReducer.user) as UserModel
  const userType = useSelector<RootState>((state) => state.userReducer.userType) as string
  const tenant = useSelector<RootState>((state) => state.userReducer.tenant) as string
  // Reactive by construction — see the hook: a prod hard-load renders this
  // route table before general settings arrive.
  const newExamManagerEnabled = useIsNewExamManagerEnabled()

  let Layout = MasterLayout
  if (isM_Layout()) {
    Layout = IframeLayout
  }

  useEffect(() => {
    if (user.id) {
      posthog?.group('tenant', tenant)
      posthog?.identify(`${user.id}`, {
        ...user,
        userType,
        tenant,
      })
    }
  }, [posthog, tenant, user, userType])

  return (
    <Routes>
      <Route element={<Layout />}>
        {/* Redirect to Dashboard after success login */}
        <Route path='auth/*' element={<Navigate replace to='/dashboard' />} />

        {/* Pages */}
        <Route
          path='attendance/student-subject/combined-report/'
          element={
            <SuspensedView>
              <StudentSubjectCombinedAttendanceReport />
            </SuspensedView>
          }
        />

        <Route
          path='fee'
          element={
            <SuspensedView>
              <LGSFeeIframe />
            </SuspensedView>
          }
        />

        {/* Digital (leave) requests — the native module, which the PHP app also
            embeds; replaces the legacy /digitalRequests/leaveRequestMessage notice */}
        {hasPermission('digital_request_manager') && (
          <Route
            path='institute/digital-requests/*'
            element={
              <SuspensedView>
                <DigitalRequestPage />
              </SuspensedView>
            }
          />
        )}

        {hasPermission('activity_manager') && (
          <Route
            path='activities'
            element={
              <SuspensedView>
                <StudentActivitiesPage />
              </SuspensedView>
            }
          />
        )}
        {hasPermission('student_history') && (
          <Route
            path='class-history'
            element={
              <SuspensedView>
                <StudentClassHistoryPage />
              </SuspensedView>
            }
          />
        )}
        {hasPermission('knowledge_base') && (
          <Route
            path='tutorials'
            element={
              <SuspensedView>
                <StudentTutorialsPage />
              </SuspensedView>
            }
          />
        )}
        {/* Lyceum hotfix: the school asked for the student/parent exam list to
            be unreachable even while the exam module is on. Hard-coded to the
            lyceum tenant only. Registered as an explicit redirect (not just
            omitted) so /exams cannot fall through to the legacy iframe
            catch-all below. The sidebar/dashboard links are hidden too. */}
        {isLyceum() ? (
          <Route path='exams' element={<Navigate to='/dashboard' replace />} />
        ) : (
          hasPermission('exam_manager_exams') && (
            <Route
              path='exams'
              element={
                <SuspensedView>
                  <StudentExamsListPage />
                </SuspensedView>
              }
            />
          )
        )}
        {hasPermission('subject_manager') && (
          <Route
            path='subjects'
            element={
              <SuspensedView>
                <StudentSubjectsPage />
              </SuspensedView>
            }
          />
        )}

        {/* Native notification inbox (replaces the /notification/admin iframe) */}
        {hasPermission('notification_manager') && (
          <Route
            path='notifications'
            element={
              <SuspensedView>
                <StudentNotificationsPage />
              </SuspensedView>
            }
          />
        )}

        {/* Native fee vouchers page; the legacy /finance/finance/view iframe
            stays reachable through the catch-all while the page is in beta */}
        {isNewFeePageEnabled() && hasPermission('fee_manager') && (
          <Route
            path='fee/vouchers'
            element={
              <SuspensedView>
                <StudentFeePage />
              </SuspensedView>
            }
          />
        )}

        <Route
          path='dashboard'
          element={
            <SuspensedView>
              <Dashboard />
            </SuspensedView>
          }
        />

        {/* Same gate as the school routes. For a student or guardian
            `message_manager_view` also carries their own module flag and the
            pro check, so all three toggles are folded into this one question. */}
        {hasPermission('message_manager_view') && (
          <Route
            path='messages/*'
            element={
              <SuspensedView>
                <ChatPage />
              </SuspensedView>
            }
          />
        )}

        <Route
          path='profile/*'
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />

        <Route
          path='institute/counselling/*'
          element={
            <SuspensedView>
              <CollegeCounsellingPage />
            </SuspensedView>
          }
        />

        <Route
          path='student/*'
          element={
            <SuspensedView>
              <StudentProfilePage />
            </SuspensedView>
          }
        />
        <Route
          path='institute/schedule/yearly-calendar'
          element={
            <SuspensedView>
              <SchedulePageYearly />
            </SuspensedView>
          }
        />
        <Route
          path='institute/schedule/daily-calendar'
          element={
            <SuspensedView>
              <SchedulePageDaily />
            </SuspensedView>
          }
        />
        <Route
          path='institute/schedule/yearly/highlights/'
          element={
            <SuspensedView>
              <SchedulePageHighlights />
            </SuspensedView>
          }
        />
        <Route
          path='institute/schedule/:id'
          element={
            <SuspensedView>
              <SchedulePageYearlyView />
            </SuspensedView>
          }
        />
        <Route
          path='institute/schedule/daily/:id'
          element={
            <SuspensedView>
              <SchedulePageDailyView />
            </SuspensedView>
          }
        />
        <Route
          path='institute/news/fullmode'
          element={
            <SuspensedView>
              <NewsPageFullmode />
            </SuspensedView>
          }
        />
        <Route
          path='institute/news/:id'
          element={
            <SuspensedView>
              <NewsPageView />
            </SuspensedView>
          }
        />

        <Route
          path='institute/events/highlights'
          element={
            <SuspensedView>
              <EventPageHighlights />
            </SuspensedView>
          }
        />

        <Route
          path='institute/events/:id'
          element={
            <SuspensedView>
              <EventPageView />
            </SuspensedView>
          }
        />

        <Route
          path='institute/events/:id/gallery'
          element={
            <SuspensedView>
              <EventPageGallery />
            </SuspensedView>
          }
        />

        <Route
          path='institute/lectures-new/list'
          element={
            <SuspensedView>
              <LecturePage />
            </SuspensedView>
          }
        />

        <Route
          path='institute/lectures-new/:id'
          element={
            <SuspensedView>
              <LectureViewPage />
            </SuspensedView>
          }
        />

        <Route path='school/*' element={<SchoolToInstitute />} />
        <Route
          path='settings'
          element={
            <SuspensedView>
              <Settings />
            </SuspensedView>
          }
        />

        <Route
          path='user-manual'
          element={
            <SuspensedView>
              <UserManualPage />
            </SuspensedView>
          }
        />
        <Route
          path='user-manual/:topic'
          element={
            <SuspensedView>
              <UserManualPage />
            </SuspensedView>
          }
        />

        {/* Native exam surfaces. Registered BEFORE the catch-all so they take
            precedence; anything under institute/exam they do not handle -- the
            parked datesheet, the legacy AJAX report card -- still falls through
            to the iframe below. */}
        {newExamManagerEnabled && hasPermission('exam_manager') && (
          <Route
            path='institute/exam/*'
            element={
              <SuspensedView>
                <StudentExamsPage />
              </SuspensedView>
            }
          />
        )}

        <Route
          path='*'
          element={
            <SuspensedView>
              <SchoolPage />
            </SuspensedView>
          }
        />

        {isDevEnv() && hasPermission('exam_manager_exam_schedules') && (
          <Route
            path='institute/exam-schedule/*'
            element={
              <SuspensedView>
                <ExamSchedulePage />
              </SuspensedView>
            }
          />
        )}

        {isDevEnv() && hasPermission('performance_report') && (
          <Route
            path='institute/performance-reports/*'
            element={
              <SuspensedView>
                <PerformanceReportsPage />
              </SuspensedView>
            }
          />
        )}

        {/* Page Not Found */}
        <Route path='*' element={<NotFound />} />
      </Route>
    </Routes>
  )
}

export default StudentRoutes
